import { useCallback, useState } from "react";
import { useSlidePanelState } from "./useSlidePanelState";

/**
 * A slide panel plus the value it shows (the row, id or launch options it was
 * opened for) - `useSlidePanelState` with a target.
 *
 * `open(value)` sets the target and opens the panel in one call; it also takes
 * an updater (`open(current => next)`), e.g. to bump a remount key. `close()`
 * never clears the target, so the panel keeps its content through the
 * slide-out: render it on `isMounted && value`, never on state the close clears.
 *
 * @example
 * const details = useSlidePanelTarget<string>();
 * // <Button onClick={() => details.open(row.id)} />
 * {details.isMounted && details.value && (
 *   <DetailsPanel open={details.isOpen} onClose={details.close} id={details.value} />
 * )}
 */
export function useSlidePanelTarget<T>() {
  const { isOpen, isMounted, open: show, close } = useSlidePanelState(false);
  const [value, setValue] = useState<T | null>(null);
  const open = useCallback(
    (next: T | ((current: T | null) => T)) => {
      setValue(next);
      show();
    },
    [show],
  );
  return { isOpen, isMounted, value, open, close };
}
